import { Link } from "react-router-dom";
import { ArrowUpRight, MessageSquare } from "lucide-react";

/**
 * Bold end-of-page conversion band.
 * Two CTAs: primary → Positioning Review funnel; secondary → Contact page.
 * Used at the bottom of long content pages (client detail, case study,
 * service page).
 */
export default function BottomCTA({
  headline = "Ready to be seen?",
  subline = "Two ways in, a full Positioning Review, or a quick message. Either way, you'll hear from a real person.",
}) {
  return (
    <section
      data-testid="bottom-cta"
      className="dark-section relative overflow-hidden bg-slate-950 text-white"
    >
      {/* Grid texture */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #22d3ee 1px, transparent 0)",
          backgroundSize: "40px 40px",
        }}
      />
      {/* Cyan glow accent */}
      <div
        aria-hidden
        className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(#22d3ee, transparent 60%)" }}
      />

      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-cyan-400" />
              <span className="label-eyebrow text-cyan-300">
                Let&apos;s talk
              </span>
            </div>
            <h2
              data-testid="bottom-cta-headline"
              className="mt-4 font-heading font-black text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tighter"
            >
              {headline.split(" ").slice(0, -1).join(" ")}{" "}
              <span className="text-accent-blue italic">
                {headline.split(" ").slice(-1)[0]}
              </span>
            </h2>
            <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-2xl">
              {subline}
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3">
            <Link
              to="/positioning-review"
              data-testid="bottom-cta-primary"
              className="group inline-flex items-center justify-between gap-2 bg-accent-blue text-white px-6 py-4 rounded-full font-heading font-semibold text-sm hover:bg-cyan-500 transition-all"
            >
              <span>Start a Positioning Review</span>
              <ArrowUpRight
                size={18}
                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
              />
            </Link>
            <Link
              to="/contact"
              data-testid="bottom-cta-secondary"
              className="group inline-flex items-center justify-between gap-2 border border-white/25 text-white px-6 py-4 rounded-full font-heading font-semibold text-sm hover:border-accent-blue hover:bg-white/5 transition-all"
            >
              <span className="inline-flex items-center gap-2">
                <MessageSquare size={16} />
                Send us a message
              </span>
              <ArrowUpRight
                size={18}
                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
              />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
